import {useMemo} from 'react'
import {Modal, StyleSheet, Text, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans} from '@lingui/react/macro'

import {useSession} from '#/state/session'
import {FlashBookPlayer} from '#/components/flashbooks/FlashBookPlayer'
import {type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {type FlashBookModel} from '#/types/flashbook'

type Props = {
  visible: boolean
  book: FlashBookModel
  /** preview only a single page (creator per-page preview) */
  initialPageIndex?: number
  isSinglePage?: boolean
  /** Start directly on the selected question, skipping the book cover. */
  startInReading?: boolean
  onClose: () => void
}

export function FlashBookPreviewModal({
  visible,
  book,
  initialPageIndex = 0,
  isSinglePage = false,
  startInReading = false,
  onClose,
}: Props) {
  const ss = useStyles()
  const chrome = useStudioChrome()
  const {currentAccount} = useSession()

  const previewBook: FlashBookModel = useMemo(
    () => ({
      ...book,
      id: 'preview',
      creatorId: currentAccount?.did ?? book.creatorId,
      status: 'draft',
    }),
    [book, currentAccount?.did],
  )

  const previewDisclaimer = (
    <View style={ss.disclaimer}>
      <Feather name="eye" size={11} color={chrome.warning} />
      <Text style={ss.disclaimerText}>
        <Trans>Preview mode – no data is saved</Trans>
      </Text>
    </View>
  )

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={onClose}>
      <View style={ss.container}>
        <FlashBookPlayer
          book={previewBook}
          isPreview
          previewFooter={previewDisclaimer}
          initialPageIndex={initialPageIndex}
          isSinglePage={isSinglePage}
          startInReading={startInReading}
          onClose={onClose}
        />
      </View>
    </Modal>
  )
}

const makeStyles = (chrome: StudioChrome) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: chrome.frame,
    },
    disclaimer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      backgroundColor: chrome.warningTint,
      borderWidth: 1,
      borderColor: chrome.warningBorder,
      borderRadius: 20,
      paddingHorizontal: 12,
      paddingVertical: 5,
      alignSelf: 'center',
      marginTop: 12,
    },
    disclaimerText: {
      color: chrome.warning,
      fontSize: 11,
    },
  })

/** Styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
